import { useId, useRef, useState } from 'react'
import type { KeyboardEvent, PointerEvent } from 'react'

import { cn } from '@/lib/utils'

export interface PanelResizeHandleProps {
  /** Current height (px) of the panel this handle resizes. */
  value: number
  min: number
  max: number
  /** Called with the new, already clamped height on every drag move / key press. */
  onResize: (heightPx: number) => void
  /** Double-click / Enter: back to the automatic height. */
  onReset: () => void
  /** Accessible name, e.g. "Resize timeline". */
  label: string
  /** Id of the resized panel (`aria-controls`). */
  controls?: string
  /** Arrow-key step in px; Shift+Arrow moves 4x. */
  step: number
  className?: string
}

interface DragState {
  pointerId: number
  startY: number
  startValue: number
}

/**
 * Horizontal splitter on a bottom panel's top edge (WAI-ARIA window
 * splitter pattern): a focusable `role="separator"` whose value is the
 * panel's height. Dragging it up makes the panel taller; ArrowUp/ArrowDown
 * resize by `step` (Shift: 4x), Home/End jump to the min/max, Enter or a
 * double-click reset to the automatic height. Pointer events (with
 * capture) cover mouse, pen and touch alike.
 */
export function PanelResizeHandle({
  value,
  min,
  max,
  onResize,
  onReset,
  label,
  controls,
  step,
  className,
}: PanelResizeHandleProps) {
  const dragRef = useRef<DragState | null>(null)
  const [dragging, setDragging] = useState(false)
  const descriptionId = useId()
  const clamp = (height: number) => Math.round(Math.min(max, Math.max(min, height)))

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    // No text selection / native drag while resizing; focus explicitly
    // (preventDefault also suppresses the focusing mousedown).
    event.preventDefault()
    const handle = event.currentTarget
    handle.focus()
    if (typeof handle.setPointerCapture === 'function') handle.setPointerCapture(event.pointerId)
    dragRef.current = { pointerId: event.pointerId, startY: event.clientY, startValue: value }
    setDragging(true)
  }

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (drag == null || event.pointerId !== drag.pointerId) return
    // Dragging up (smaller clientY) grows the panel.
    const next = clamp(drag.startValue + (drag.startY - event.clientY))
    if (next !== value) onResize(next)
  }

  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (drag == null || event.pointerId !== drag.pointerId) return
    dragRef.current = null
    setDragging(false)
    const handle = event.currentTarget
    if (
      typeof handle.hasPointerCapture === 'function' &&
      handle.hasPointerCapture(event.pointerId)
    ) {
      handle.releasePointerCapture(event.pointerId)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const delta = event.shiftKey ? step * 4 : step
    let next: number
    switch (event.key) {
      case 'ArrowUp':
        next = value + delta
        break
      case 'ArrowDown':
        next = value - delta
        break
      case 'Home':
        next = min
        break
      case 'End':
        next = max
        break
      case 'Enter':
        event.preventDefault()
        onReset()
        return
      default:
        return
    }
    event.preventDefault()
    const clamped = clamp(next)
    if (clamped !== value) onResize(clamped)
  }

  return (
    <>
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label={label}
        aria-controls={controls}
        aria-valuenow={value}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuetext={`${value} pixels`}
        aria-describedby={descriptionId}
        tabIndex={0}
        title="Drag to resize · double-click to reset"
        data-dragging={dragging ? '' : undefined}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
        onKeyDown={handleKeyDown}
        onDoubleClick={onReset}
        className={cn(
          'group focus-visible:ring-ring flex h-3 cursor-row-resize touch-none items-center justify-center rounded-sm outline-none select-none focus-visible:ring-2',
          className,
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'h-1 w-12 rounded-full transition-colors',
            dragging
              ? 'bg-primary'
              : 'bg-border group-hover:bg-primary group-focus-visible:bg-primary',
          )}
        />
      </div>
      <span id={descriptionId} className="sr-only">
        Use the up and down arrow keys to resize (Shift for larger steps), Home and End for the
        smallest and largest size, and Enter to reset to the automatic size.
      </span>
    </>
  )
}
